<template>
  <li class="file-row">
    <span v-show="isFileAlreadyInLibrary && !uploadStatus" class="duplicate-file-warning" >

      <b-tooltip label="There is another file in your library with the same name. Continuing will replace the existing file unless you name the new file."
                 multilined
                 position="is-right">
            <font-awesome-icon color="orange" icon="exclamation-triangle" />
          </b-tooltip>
    </span>
    <b-select class="category-selector" placeholder="Document Category" :disabled="uploadStatus" size="is-small" v-model="category">
      <option disabled>Please select a document category</option>
      <option v-for="(displayName, key) in categoryList" :value="key" :key="key">
        {{displayName}}
      </option>
    </b-select>
    <b-switch  v-model="addToLibrary" :disabled="uploadStatus !== null" v-if="canAddToCompanyLib" />
    <div class="file-name">
      <input class="input is-small" :disabled="uploadStatus" v-model="userInputFileName" />
    </div>
    <div>
      <div class="icons">
        <div class="delete-icon" @click="remove" v-show="!uploadStatus">
          <font-awesome-icon icon="times" />
        </div>
        <font-awesome-icon icon="spinner" spin v-show="uploadStatus === 'in-progress'" />

        <div class="has-text-danger" @click="remove" v-show="uploadStatus === 'error'">
          <b-tooltip label="Error uploading this file. Click here to remove it and try again."
                     position="is-left">
            <font-awesome-icon icon="exclamation"  />
          </b-tooltip>
        </div>

        <div v-show="uploadStatus === 'success'" >
          <b-tooltip label="Successfully uploaded this file" position="is-left">
            <font-awesome-icon icon="check" class="has-text-success"  />
          </b-tooltip>
        </div>
      </div>
    </div>
  </li>
</template>

<script>
import { mapGetters } from 'vuex'
import getBase64 from '@/helpers/fileToBase64'
import cleanS3FileName from '@/helpers/makeS3SafeFileName'
import categoryList from '@/lookup-data/document-types-uploadable.json'
import DocumentsApi from '@/api-interface/document'
import { isEmpty, uniqueId } from 'lodash-es'

export default {
  name: 'DocumentUploader-row',

  props: {
    value: {
      type: File,
      required: true
    },
    canAddToCompanyLib: {
      type: Boolean,
      default: true
    },
    targetDocumentHolderName: {
      type: String,
      required: true
    },
    targetDocumentHolderKey: {
      type: String,
      required: true
    }
  },

  created() {
    this.extractFileName()
  },

  computed: {
    ...mapGetters('companyDocuments', { existingLibraryFiles: 'allFileNames' }),
    ...mapGetters('user', ['accountInfo']),

    /**
     * Boolean to indicate if all required attributes are present
     *
     * @returns {Boolean}
     */
    readyForUpload() {
      return !isEmpty(this.category) && !isEmpty(this.customFileName.name)
    },

    /**
     * Boolean to indicate if a file with the same name is already present in the library
     *
     * @returns {Boolean}
     */
    isFileAlreadyInLibrary() {
      // only perform the duplicate name check when dealing with company library, not transaction files
      if ((this.addToLibrary || this.targetDocumentHolderName === 'company') && !this.uploadStatus) {
        const newFileName = this.finalFileName
        return this.existingLibraryFiles.includes(newFileName)
      } else {
        return false
      }
    },

    /**
     * Combines the custom file name (entered by the user) with the file extension
     *
     * @returns {String}
     */
    finalFileName() {
      return `${this.customFileName.name}.${this.customFileName.extension}`
    },

    /**
     * Cleans up the custom name typed in by the user to make sure all characters are S3 safe
     */

    userInputFileName: {
      set(input) {
        this.customFileName.name = cleanS3FileName(input)
      },
      get() {
        return this.customFileName.name
      }
    }
  },

  data() {
    return {
      uuid: uniqueId('upload-'),
      categoryList,
      addToLibrary: false,
      uploadStatus: null,
      customFileName: {
        name: '',
        extension: ''
      },
      category: '',
      uploadResponse: {}
    }
  },

  methods: {
    /**
     * Separates file name from file extension so the user can change the file name.
     */
    extractFileName() {
      let name = this.value.name.split('.')
      this.customFileName.extension = name.pop()
      this.customFileName.name = cleanS3FileName(name.join('.'))
    },

    /**
     * Notify parent that a file needs to be removed from the list
     */

    remove() {
      this.$emit('delete')
    },

    /**
     * Takes documents that have already been uploaded (to the companyJob) and copy them to the company lib.
     */
    async copyToLibrary() {
      await DocumentsApi.copy({
        documentKey: this.uploadResponse.documentKey,
        copyToDocumentHolderKey: this.accountInfo.company.companyKey,
        copyToDocumentHolder: 'company'
      })
    },

    /**
     * Actually perform the upload
     *
     * @returns {Promise<void>}
     */
    async upload() {
      if (this.uploadStatus) {
        // do nothing if file upload was already attempted
        return
      }

      this.uploadStatus = 'in-progress'

      const payload = {
        documentName: this.finalFileName,
        documentHolder: this.targetDocumentHolderName,
        documentType: this.category,
        documentHolderKey: this.targetDocumentHolderKey,
        documentData: await getBase64(this.value)
      }

      try {
        this.uploadResponse = await DocumentsApi.upload(payload, this.isFileAlreadyInLibrary)

        if (this.addToLibrary) {
          await this.copyToLibrary()
        }

        this.uploadStatus = 'success'
      } catch (error) {
        this.uploadStatus = 'error'
        console.error(`Failed to upload ${payload.documentName} : ${error}`)
      } finally {
        this.$emit('upload-done', { uuid: this.uuid })
      }
    }
  },

  watch: {
    isFileAlreadyInLibrary() {
      this.$emit('duplicate', {
        uuid: this.uuid,
        name: this.customFileName.name,
        isPresent: this.isFileAlreadyInLibrary
      })
    },
    readyForUpload(newValue) {
      this.$emit('can-upload', newValue)
    }
  }
}
</script>

<style scoped lang="scss">
.category-selector {
  width: 180px;
  margin: 0 0 0 8px;
}

.duplicate-file-warning {
  position: absolute;
  left: -10px;
  top: 0;
}

.file-row {
  margin: 8px;
  // flex: 1;
  min-height: 18px;
  height: 18px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background: white;
  position: relative;

  .delete-icon {
    height: 10px;
    width: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    &:hover {
      cursor: pointer;
      color: red;
    }
  }
}
.file-name {
  // flex: 1;
  width: 200px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  border: none;
}
</style>
